import React from 'react'

import { ZariContext } from '_zari/context'
import { Modal, Button, Form, Col } from '_zari/core'

import * as UbicacionService from 'services/ubicacionAlmacenamiento.service'

import { etiquetaCelda, filaALetra } from '../utils/etiqueta'

const MatrizEstanteria = ({ estanteria, ubicaciones, onClickCelda, onMovido, productoResaltado }) => {
  const __zariContext = React.useContext(ZariContext)

  const filas = estanteria.filas || 1
  const columnas = estanteria.columnas || 1
  const profundidad = estanteria.profundidad || 1
  const color = estanteria.color || '#1F3A8A'

  const [profundidadActiva, setProfundidadActiva] = React.useState(1)
  const [celdaHover, setCeldaHover] = React.useState(null)
  const [arrastrando, setArrastrando] = React.useState(null)
  const [celdaSobre, setCeldaSobre] = React.useState(null)
  const [confirmacion, setConfirmacion] = React.useState(null)

  const mapaCeldas = React.useMemo(() => {
    const m = {}
    for (const u of ubicaciones || []) {
      const k = `${u.fila}|${u.columna}|${u.profundidad}`
      m[k] = u
    }
    return m
  }, [ubicaciones])

  const obtenerCelda = (f, c, p) => mapaCeldas[`${f}|${c}|${p}`]

  const filasArr = []
  for (let f = filas; f >= 1; f--) filasArr.push(f)
  const columnasArr = []
  for (let c = 1; c <= columnas; c++) columnasArr.push(c)

  const totalItems = ubicaciones.reduce((s, u) => s + (u.items?.length || 0), 0)

  // === Drag & drop ===
  const onDragStartItem = (e, ubicacion, item) => {
    const payload = {
      idUbicacion: ubicacion._id,
      idItem: item._id,
      nombreProducto: item.nombreProducto,
      cantidad: item.cantidad
    }
    setArrastrando(payload)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', JSON.stringify(payload))
  }
  const onDragEndItem = () => {
    setArrastrando(null)
    setCeldaSobre(null)
  }
  const onDragOverCelda = (e, celda) => {
    if (!arrastrando || !celda) return
    if (celda._id === arrastrando.idUbicacion) return
    if (celda.bloqueado) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (celdaSobre !== celda._id) setCeldaSobre(celda._id)
  }
  const onDragLeaveCelda = (celda) => {
    if (celdaSobre === celda?._id) setCeldaSobre(null)
  }
  const onDropCelda = (e, celda) => {
    e.preventDefault()
    setCeldaSobre(null)
    if (!arrastrando || !celda) return
    if (celda._id === arrastrando.idUbicacion) {
      setArrastrando(null)
      return
    }
    if (celda.bloqueado) {
      __zariContext.snack('error', 'La celda destino está bloqueada')
      setArrastrando(null)
      return
    }

    const etiquetaDestino = etiquetaCelda(celda.fila, celda.columna, celda.profundidad, profundidad)
    setConfirmacion({
      origen: arrastrando,
      destino: { _id: celda._id, codigo: celda.codigo, etiqueta: etiquetaDestino },
      cantidad: arrastrando.cantidad
    })
    setArrastrando(null)
  }

  const ejecutarMovimiento = async () => {
    if (!confirmacion) return
    const cantTotal = confirmacion.origen.cantidad
    const cantMover = Number(confirmacion.cantidad)
    if (!Number.isFinite(cantMover) || cantMover <= 0 || cantMover > cantTotal) {
      __zariContext.snack('error', 'Cantidad inválida')
      return
    }
    try {
      await UbicacionService.moverItem(
        confirmacion.origen.idUbicacion,
        confirmacion.origen.idItem,
        confirmacion.destino._id,
        cantMover
      )
      __zariContext.snack('success', 'Item movido correctamente')
      setConfirmacion(null)
      onMovido && onMovido()
    } catch (err) {
      __zariContext.snack('error', err?.data?.error || 'Error al mover el item')
    }
  }

  return (
    <div className='ub-matriz-wrapper'>
      <div className='ub-matriz-info'>
        <div>
          <strong>{estanteria.nombre}</strong>
          <small className='text-muted ml-2'>
            {filas} fila{filas > 1 ? 's' : ''} × {columnas} columna{columnas > 1 ? 's' : ''}
            {profundidad > 1 && ` × ${profundidad} profundidad`}
          </small>
        </div>
        <div className='ub-matriz-resumen'>
          {totalItems} {totalItems === 1 ? 'producto registrado' : 'productos registrados'}
        </div>
      </div>

      {profundidad > 1 && (
        <div className='ub-prof-tabs'>
          <span className='ub-prof-label'>Profundidad:</span>
          {Array.from({ length: profundidad }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`ub-prof-tab ${profundidadActiva === p ? 'activa' : ''}`}
              onClick={() => setProfundidadActiva(p)}
              style={profundidadActiva === p ? { background: color, borderColor: color } : {}}
            >
              Nivel {p}
            </button>
          ))}
        </div>
      )}

      <div className='ub-matriz-leyenda'>
        <div className='ub-leyenda-item'>
          <span className='ub-leyenda-box vacia' />
          Vacía
        </div>
        <div className='ub-leyenda-item'>
          <span className='ub-leyenda-box ocupada' />
          Con productos
        </div>
        <div className='ub-leyenda-item'>
          <span className='ub-leyenda-box bloqueada' />
          Bloqueada
        </div>
        <div className='ub-leyenda-item ub-leyenda-tip'>
          <i className='fa fa-arrows-alt' /> Arrastra el ícono para mover items entre celdas
        </div>
      </div>

      <div className='ub-matriz-layout'>
        <div className='ub-matriz-cinema'>
          <div className='ub-cinema-marquesina' style={{ background: color }}>
            <i className='fa fa-archive mr-2' /> Frente de la estantería
          </div>

          <div className='ub-cinema-grid'>
            <div className='ub-cinema-fila-cabecera'>
              <span className='ub-cinema-cabecera-cell' />
              {columnasArr.map((c) => (
                <span key={c} className='ub-cinema-cabecera-cell'>
                  {c}
                </span>
              ))}
            </div>

            {filasArr.map((f) => (
              <div key={f} className='ub-cinema-fila'>
                <span className='ub-cinema-fila-label'>{filaALetra(f)}</span>
                {columnasArr.map((c) => {
                  const celda = obtenerCelda(f, c, profundidadActiva)
                  if (!celda) return <span key={c} className='ub-cinema-celda inexistente' />

                  const numItems = celda.items?.length || 0
                  let claseEstado = 'vacia'
                  if (celda.bloqueado) claseEstado = 'bloqueada'
                  else if (numItems > 0) claseEstado = 'ocupada'

                  const esTarget = celdaSobre === celda._id
                  const esResaltada =
                    productoResaltado &&
                    (celda.items || []).some((it) => it._idProducto === productoResaltado)

                  const etiqueta = etiquetaCelda(f, c, profundidadActiva, profundidad)

                  return (
                    <div
                      key={c}
                      className={`ub-cinema-celda ${claseEstado} ${esTarget ? 'drag-target' : ''} ${
                        esResaltada ? 'resaltada' : ''
                      }`}
                      onClick={() => onClickCelda(celda)}
                      onMouseEnter={() => setCeldaHover(celda)}
                      onMouseLeave={() => setCeldaHover((prev) => (prev && prev._id === celda._id ? null : prev))}
                      onDragOver={(e) => onDragOverCelda(e, celda)}
                      onDragLeave={() => onDragLeaveCelda(celda)}
                      onDrop={(e) => onDropCelda(e, celda)}
                    >
                      {celda.bloqueado ? (
                        <i className='fa fa-lock' />
                      ) : numItems > 0 ? (
                        <>
                          <span className='ub-celda-num'>{numItems}</span>
                          <span
                            className='ub-celda-drag-handle'
                            draggable={numItems === 1}
                            onDragStart={(e) => {
                              if (numItems === 1) {
                                e.stopPropagation()
                                onDragStartItem(e, celda, celda.items[0])
                              } else {
                                e.preventDefault()
                                __zariContext.snack(
                                  'info',
                                  'Esta celda tiene varios productos. Ábrela para mover uno específico.'
                                )
                              }
                            }}
                            onDragEnd={onDragEndItem}
                            onClick={(e) => e.stopPropagation()}
                            title={
                              numItems === 1
                                ? 'Arrastra para mover este producto'
                                : 'Varios productos: abrir la celda para mover'
                            }
                          >
                            <i className='fa fa-arrows-alt' />
                          </span>
                        </>
                      ) : (
                        <span className='ub-celda-vacio'>—</span>
                      )}
                      <span className='ub-celda-cod'>{etiqueta}</span>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>

        <PanelDetalle celda={celdaHover} profundidad={profundidad} />
      </div>

      <div className='ub-matriz-piso'>
        <i className='fa fa-info-circle mr-1' />
        Click en una celda para asignar productos, ver el contenido o bloquearla. Arrastra el ícono <i className='fa fa-arrows-alt' /> para mover ítems.
      </div>

      {confirmacion && (
        <Modal show={true} size='sm'>
          <Modal.Header>
            <Modal.Title>
              <i className='fa fa-arrows-alt mr-2' style={{ color: '#1F3A8A' }} />
              Mover producto
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='ub-mover-resumen'>
              <div className='ub-mover-prod'>
                <strong>{confirmacion.origen.nombreProducto}</strong>
              </div>
              <div className='ub-mover-flecha'>
                <span className='ub-mover-disponible'>Disponible: <strong>{confirmacion.origen.cantidad}</strong></span>
              </div>
              <div className='ub-mover-destino'>
                Destino: <strong>{confirmacion.destino.etiqueta}</strong>{' '}
                <span className='text-muted'>({confirmacion.destino.codigo})</span>
              </div>
            </div>

            <Form.Group className='mt-3'>
              <Form.Label text='CANTIDAD A MOVER' md={12} />
              <Col md={12}>
                <Form.InputNumber
                  value={confirmacion.cantidad}
                  onChange={(e) =>
                    setConfirmacion({ ...confirmacion, cantidad: e.target.value })
                  }
                  min={0}
                  max={confirmacion.origen.cantidad}
                  id='input-cant-mover'
                  autoFocus
                />
              </Col>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button outline color='secondary' onClick={() => setConfirmacion(null)}>
              Cancelar
            </Button>
            <Button color='primary' onClick={ejecutarMovimiento}>
              <i className='fa fa-check mr-1' />
              Confirmar
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}

const PanelDetalle = ({ celda, profundidad }) => {
  if (!celda) {
    return (
      <aside className='ub-detalle-panel ub-detalle-vacio'>
        <i className='fa fa-mouse-pointer fa-2x' />
        <p>Pasa el cursor sobre una celda para ver sus detalles aquí.</p>
      </aside>
    )
  }

  const items = celda.items || []
  const etiqueta = etiquetaCelda(celda.fila, celda.columna, celda.profundidad, profundidad)

  return (
    <aside className='ub-detalle-panel'>
      <div className='ub-detalle-header'>
        <strong>{etiqueta}</strong>
        <span className='ub-detalle-cod'>{celda.codigo}</span>
      </div>
      {celda.descripcion && <div className='ub-detalle-desc'>{celda.descripcion}</div>}
      {celda.bloqueado ? (
        <div className='ub-detalle-bloq'>
          <i className='fa fa-lock' /> Celda bloqueada
          {celda.notas && <div className='ub-detalle-notas'>{celda.notas}</div>}
        </div>
      ) : items.length === 0 ? (
        <div className='ub-detalle-vacia-celda'>
          <i className='fa fa-inbox' />
          <p>Sin productos asignados</p>
        </div>
      ) : (
        <>
          <div className='ub-detalle-titulo'>
            {items.length} {items.length === 1 ? 'producto' : 'productos'}
          </div>
          <ul className='ub-detalle-items'>
            {items.map((it) => (
              <li key={it._id}>
                <div className='ub-detalle-item-cantidad'>{it.cantidad}</div>
                <div className='ub-detalle-item-info'>
                  <div className='ub-detalle-item-nombre'>{it.nombreProducto}</div>
                  {it.codigoProducto && (
                    <div className='ub-detalle-item-cod'>{it.codigoProducto}</div>
                  )}
                  {it.notas && <div className='ub-detalle-item-notas'>{it.notas}</div>}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {celda.notas && !celda.bloqueado && (
        <div className='ub-detalle-notas'>
          <i className='fa fa-sticky-note mr-1' />
          {celda.notas}
        </div>
      )}
    </aside>
  )
}

export default MatrizEstanteria
